import type { Migration, SnapshotHistoryEntry } from '../types/migration';

/**
 * Global Snapshot Hub aggregation.
 *
 * The hub lets operators browse and compare snapshots across *all* migrations
 * from one place instead of opening each job. This module contains the pure,
 * side-effect-free logic that flattens every migration's `snapshotHistory` into
 * a single filterable/sortable list, plus small display helpers. Keeping this
 * out of the page component makes the filtering behavior unit-testable.
 */

/** A single snapshot flattened out of a migration, carrying its parent context. */
export interface SnapshotHubRow {
    /** The migration this snapshot belongs to. */
    migrationId: string;
    /** The migration/snapshot schedule display name. */
    migrationName: string;
    /** Source org name (the org the snapshot was captured from). */
    orgName: string;
    /** Source org id. */
    orgId: string;
    /** ISO timestamp the snapshot was captured. */
    capturedAtUtc: string;
    /** Blob storage path for the snapshot artifact. */
    blobPath: string;
    /** Snapshot status (e.g. "Complete", "Failed"). */
    status: string;
    /** Number of files in the snapshot, when known. */
    fileCount?: number | undefined;
    /** Total snapshot size in bytes, when known. */
    totalBytes?: number | undefined;
}

/** Filter options for the hub list. All fields are optional (absent = no filter). */
export interface SnapshotHubFilter {
    /** Restrict to a single source org id. */
    orgId?: string | undefined;
    /** Only snapshots captured on/after this ISO date (inclusive). */
    fromDate?: string | undefined;
    /** Only snapshots captured on/before this ISO date (inclusive, end-of-day). */
    toDate?: string | undefined;
}

/**
 * Flattens every migration's snapshot history into a single list of hub rows.
 * Migrations without snapshot history contribute nothing. The result is sorted
 * newest-first by capture time.
 *
 * @param migrations All migrations available to the current customer.
 */
export function flattenSnapshots(migrations: readonly Migration[]): SnapshotHubRow[] {
    const rows: SnapshotHubRow[] = [];

    for (const migration of migrations) {
        const history: readonly SnapshotHistoryEntry[] = migration.snapshotHistory ?? [];
        for (const entry of history) {
            rows.push({
                migrationId: migration.id,
                migrationName: migration.name,
                orgName: migration.sourceOrgName,
                orgId: migration.sourceOrgId,
                capturedAtUtc: entry.capturedAtUtc,
                blobPath: entry.blobPath,
                status: entry.status,
                fileCount: entry.fileCount,
                totalBytes: entry.totalBytes,
            });
        }
    }

    return sortSnapshotsNewestFirst(rows);
}

/** Sorts hub rows newest-first by capture time (invalid dates sink to the end). */
export function sortSnapshotsNewestFirst(rows: readonly SnapshotHubRow[]): SnapshotHubRow[] {
    return [...rows].sort((a, b) => {
        const ta = Date.parse(a.capturedAtUtc);
        const tb = Date.parse(b.capturedAtUtc);
        const va = Number.isNaN(ta) ? -Infinity : ta;
        const vb = Number.isNaN(tb) ? -Infinity : tb;
        return vb - va;
    });
}

/**
 * Applies an org/date filter to a list of hub rows. Absent filter fields are
 * treated as "no restriction". The `toDate` bound is inclusive to end-of-day so
 * a same-day `fromDate`/`toDate` pair still matches snapshots taken that day.
 */
export function filterSnapshots(
    rows: readonly SnapshotHubRow[],
    filter: SnapshotHubFilter
): SnapshotHubRow[] {
    const fromMs = filter.fromDate ? Date.parse(filter.fromDate) : null;
    const rawTo = filter.toDate ? Date.parse(filter.toDate) : null;
    // Push an inclusive end-of-day bound (23:59:59.999) when a toDate is given.
    const toMs =
        rawTo !== null && !Number.isNaN(rawTo) ? rawTo + (24 * 60 * 60 * 1000 - 1) : null;

    return rows.filter((row) => {
        if (filter.orgId && row.orgId !== filter.orgId) return false;

        const captured = Date.parse(row.capturedAtUtc);
        if (fromMs !== null && !Number.isNaN(fromMs) && !Number.isNaN(captured) && captured < fromMs) {
            return false;
        }
        if (toMs !== null && !Number.isNaN(captured) && captured > toMs) {
            return false;
        }
        return true;
    });
}

/**
 * Returns the distinct source orgs that own at least one snapshot, sorted by
 * name — used to populate the hub's org filter dropdown.
 */
export function getSnapshotOrgOptions(
    rows: readonly SnapshotHubRow[]
): { id: string; name: string }[] {
    const byId = new Map<string, string>();
    for (const row of rows) {
        if (!byId.has(row.orgId)) byId.set(row.orgId, row.orgName);
    }
    return [...byId.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Formats a byte count as a short human-readable size (e.g. "1.5 MB"), or a
 * dash when the size is unknown.
 */
export function formatSnapshotSize(totalBytes?: number): string {
    if (totalBytes === undefined || totalBytes < 0 || Number.isNaN(totalBytes)) return '—';
    if (totalBytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exponent = Math.min(
        Math.floor(Math.log(totalBytes) / Math.log(1024)),
        units.length - 1
    );
    const value = totalBytes / Math.pow(1024, exponent);
    const rounded = exponent === 0 ? String(value) : value.toFixed(1);
    return `${rounded} ${units[exponent]}`;
}
